import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SitesService } from 'src/app/core/services/api/sites.service';
import { AppSettingsService } from 'src/app/core/services/app-settings.service';

@Component({
  selector: 'app-dynamic-input-modal',
  templateUrl: './dynamic-input-modal.component.html',
  styleUrls: ['./dynamic-input-modal.component.scss'],
})
export class DynamicInputModalComponent implements OnInit, OnDestroy {
  @Input() modalTitle = 'site_setup_create_title';
  @Input() inputLabel: string;
  @Input() inputValue = '';
  validName: boolean | undefined;
  storeDomain = '';
  isAdding = false;
  isChecking = false;
  validationError = false;
  // Keep the existing alphanumeric input, with the letter required by CreateSiteRequest.
  readonly lettersOnlyPattern = /^(?=.*[A-Za-z])[A-Za-z0-9]+$/;
  private validatedName = '';
  private validation?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private sitesService: SitesService,
    private appSettingsService: AppSettingsService
  ) {}

  ngOnInit(): void {
    this.storeDomain = this.appSettingsService.getStoreDomain();
    if (this.inputValue) this.validateShopName();
  }

  get normalizedName(): string { return (this.inputValue || '').toLowerCase(); }
  get validFormat(): boolean { return this.inputValue.length >= 3 && this.lettersOnlyPattern.test(this.inputValue); }
  get canSubmit(): boolean {
    return this.validFormat && this.validName === true && this.validatedName === this.normalizedName && !this.isChecking && !this.isAdding;
  }
  get nameInvalid(): boolean { return !!this.inputValue && (!this.validFormat || this.validName === false); }
  get validationKey(): string {
    if (this.isChecking) return 'site_setup_checking_name';
    if (this.validationError) return 'site_setup_name_check_error';
    if (!this.inputValue) return 'site_setup_name_hint';
    if (this.inputValue.length < 3) return 'auth_shop_name_min';
    if (!this.lettersOnlyPattern.test(this.inputValue)) return 'site_setup_name_characters';
    if (this.validName === false) return 'auth_shop_name_exists';
    if (this.validName === true) return 'auth_shop_name_available';
    return 'site_setup_name_hint';
  }

  validateShopName(value: string = this.inputValue): void {
    this.inputValue = value || '';
    this.validation?.unsubscribe();
    this.validName = undefined;
    this.validatedName = '';
    this.validationError = false;
    this.isChecking = this.validFormat;
    if (!this.validFormat) return;

    const name = this.normalizedName;
    this.validation = timer(300).pipe(
      switchMap(() => this.sitesService.validateSiteName({ site_name: name }))
    ).subscribe({
      next: () => {
        if (name !== this.normalizedName) return;
        this.validName = true;
        this.validatedName = name;
        this.isChecking = false;
      },
      error: (error) => {
        if (name !== this.normalizedName) return;
        this.isChecking = false;
        if (error.status === 409) this.validName = false;
        else this.validationError = true;
      }
    });
  }

  onAdd(): void {
    if (!this.canSubmit) return;
    this.isAdding = true;
    this.activeModal.close(this.normalizedName);
  }

  ngOnDestroy(): void { this.validation?.unsubscribe(); }
}
